import _extends from '@babel/runtime/helpers/extends';
import React__default, { useState, useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { i as inputFocusStyles } from '../InputFormGroup/InputFormGroup.styles-CTSi41iT.js';
import { s as styleTextAreaMixin } from '../TextAreaFormGroup/TextAreaFormGroup-SOFmCY4E.js';
import { A as AttachmentPill } from './AttachmentPill-CjOl6xA9.js';
import { V as ValidationLine } from '../FormGroup/ValidationLine-CUeG-6l1.js';
import { F as FileAttachmentButton } from './FileAttachmentButton-BRKI92s7.js';
import { S as SendButton } from './SendButton-DP0q8Fpi.js';
import { g as getTranslationComponent } from '../i18n/ChatBoxInputTranslation/index-BxB2jbOV.js';
import { T as ThemeProvider } from '../theme/ThemeProvider-CyHUD0Ec.js';
import { T as TOOLTIP_DELAY_MS, B as BASE_INPUT_HEIGHT, M as MAX_ATTACHMENTS, a as MAX_SIZE_MB, b as MAX_ROWS, I as INPUT_PADDING } from './constants-Di8x-WZD.js';
import { v as verifyAttachmentType } from './utils/verifyAttachmentType-DyWhJua4.js';
import { I as IntlProvider } from '../LocalModules/storm-ui-intl/dist/esm/IntlProvider-CX9yP88S-B6OFY9Vv.js';
import { u as useIntl } from '../LocalModules/storm-ui-intl/dist/esm/3rdPartyModules/react-intl/lib/src/components/useIntl-BGx4v_Uj-CJNeXUZQ.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-AmBipDtf-AmBipDtf.js';
import { v as v4 } from '../3rdPartyModules/uuid/dist/esm-browser/v4-BuAr0E2J.js';
import { T as TaktIdConsumer } from '../TaktIdContext/TaktIdContext-DlOuPWuU.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-DaCk9T1Y.js';
import { I as InlineMessage } from '../Text/InlineMessage/InlineMessage-DsdJkOoL.js';

const overrideMouseTimeout = theme => ({
  ...theme,
  tooltip: {
    ...theme.tooltip,
    mouseEnterTimeout: TOOLTIP_DELAY_MS,
    mouseLeaveTimeout: 0
  }
});
function getContainerBackgroundColor(theme, disabled, drag) {
  if (disabled) {
    return theme.form.input.disabled.bg;
  }
  if (drag) {
    return theme.palette.blue[50];
  }
  return theme.form.input.bg;
}
const Container = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30062554__sc-nwig80-0"
})(["border:", ";border-color:", ";border-style:", " box-shadow:", ";box-sizing:border-box;border-radius:", ";background:", ";color:", ";:focus-within{", "}border-style:", ""], ({
  theme
}) => theme.form.input.border, ({
  theme
}) => theme.form.input.borderColor, ({
  $drag
}) => $drag ? 'dashed' : 'solid', ({
  theme
}) => theme.form.input.boxShadow, ({
  theme
}) => theme.form.input.radius, ({
  theme,
  disabled,
  $drag
}) => getContainerBackgroundColor(theme, disabled, $drag), ({
  theme,
  disabled
}) => disabled ? theme.form.input.disabled.color : theme.form.input.color, inputFocusStyles, ({
  $drag
}) => $drag ? 'dotted' : 'solid');
const InputGroup = /*#__PURE__*/styled(({
  children,
  inputGroupRef
}) => /*#__PURE__*/React__default.createElement("div", {
  ref: inputGroupRef
}, children)).withConfig({
  componentId: "sc-storm-ui-30062554__sc-nwig80-1"
})(["border:none;box-shadow:", ";box-sizing:border-box;border-radius:", ";min-width:auto;display:flex;pointer-events:", ";> textarea{", " pointer-events:", ";}"], ({
  theme
}) => theme.form.input.boxShadow, ({
  theme
}) => theme.form.input.radius, ({
  $drag
}) => $drag ? 'none' : 'auto', styleTextAreaMixin, ({
  $drag
}) => $drag ? 'none' : 'auto');
const AttachmentRow = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30062554__sc-nwig80-2"
})(["display:flex;justify-content:flex-start;align-items:flex-end;gap:", ";padding:", ";flex-wrap:wrap;"], ({
  theme
}) => theme.spacing.micro, ({
  theme
}) => `${theme.spacing.micro} ${theme.spacing.small}`);
const ActionBarWrapper = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30062554__sc-nwig80-3"
})(["display:flex;justify-content:space-between;align-items:center;padding:", ";"], ({
  theme
}) => `${theme.spacing.micro} ${theme.spacing.mini}`);
const ValidationLoadingWrapper = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30062554__sc-nwig80-4"
})(["height:40px;padding:5px;display:flex;align-items:center;"]);
const ActionBar = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30062554__sc-nwig80-5"
})(["width:100%;"]);
const StyledTextArea = /*#__PURE__*/styled('textarea').withConfig({
  componentId: "sc-storm-ui-30062554__sc-nwig80-6"
})(["", " resize:none;max-height:", "px;overflow:auto;box-sizing:border-box;"], styleTextAreaMixin, BASE_INPUT_HEIGHT * MAX_ROWS - MAX_ROWS - INPUT_PADDING);
const MessageContainer = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30062554__sc-nwig80-7"
})(["margin-top:", ";display:flex;flex-direction:column;gap:", ";"], ({
  theme
}) => theme.spacing.micro, ({
  theme
}) => theme.spacing.micro);
const ChatBoxValidationMessage = /*#__PURE__*/styled(InlineMessage).withConfig({
  componentId: "sc-storm-ui-30062554__sc-nwig80-8"
})(["margin-top:0;margin-bottom:0;"]);
const ChatBox = ({
  id,
  value = '',
  attachments: inputAttachments,
  disabled,
  placeholder,
  inputRef,
  locale,
  onChange,
  onAttach,
  onSend,
  renderActionBar,
  taktId,
  taktValue,
  onAttachmentClose,
  errorMessage,
  validateAttachment,
  acceptedAttachmentTypes,
  ...rest
}) => {
  const [isActiveDrag, setActiveDrag] = useState(false);
  const [errors, setErrors] = useState(errorMessage ? [errorMessage] : []);
  const [inputValue, setInputValue] = useState(value);
  const getInitialAttachments = () => {
    const initialAttachments = [];
    if (inputAttachments) {
      for (const att of inputAttachments) {
        const uniqueId = v4();
        initialAttachments.push({
          file: att,
          id: uniqueId
        });
      }
    }
    return initialAttachments;
  };
  const [attachments, setAttachments] = useState(getInitialAttachments);
  const [isValidating, setIsValidating] = useState(false);
  const textAreaRef = useRef(null);
  const {
    formatMessage
  } = useIntl();
  useLayoutEffect(() => {
    /** Adjust size at render */
    resize();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resize = useCallback((clearInput = false) => {
    if (textAreaRef.current) {
      if (clearInput) {
        textAreaRef.current.innerText = '';
        textAreaRef.current.style.height = `${BASE_INPUT_HEIGHT}px`;
        return;
      }
      // removing the height allows us to validate the scroll height
      textAreaRef.current.style.removeProperty('height');
      if (textAreaRef.current.scrollHeight > BASE_INPUT_HEIGHT) {
        const updatedHeight = textAreaRef.current.scrollHeight;
        textAreaRef.current.style.height = `${updatedHeight}px`;
      } else {
        textAreaRef.current.style.height = `${BASE_INPUT_HEIGHT}px`;
      }
    }
  }, []);
  const handleChange = useCallback(event => {
    onChange === null || onChange === void 0 || onChange(event);
    setInputValue(event.target.value);
    resize();
  }, [onChange, resize]);
  const handleSend = useCallback(() => {
    const files = attachments.map(attachment => attachment.file);
    onSend === null || onSend === void 0 || onSend(inputValue, files);
    setInputValue('');
    setAttachments([]);
    setErrors(errorMessage ? [errorMessage] : []);
    resize(true);
  }, [attachments, errorMessage, inputValue, onSend, resize]);
  const handleKeydown = useCallback(event => {
    if (event.key === KEYBOARD_KEYNAMES.ENTER) {
      event.preventDefault();
      handleSend();
    }
  }, [handleSend]);
  const handleAttachmentClose = useCallback((file, index) => {
    const filteredAttachments = attachments.filter((_att, attachmentIndex) => attachmentIndex !== index);
    setAttachments(filteredAttachments);
    if (filteredAttachments.length <= MAX_ATTACHMENTS) {
      setErrors(errorMessage ? [errorMessage] : []);
    }
    onAttachmentClose === null || onAttachmentClose === void 0 || onAttachmentClose(file, index);
  }, [attachments, errorMessage, onAttachmentClose]);
  const handleFileUpload = useCallback(async (event, fileList) => {
    const validFiles = [];
    const fileErrors = [];
    let attachmentCount = attachments.length;
    for await (const file of fileList) {
      // Cannot attach more than 6 files
      if (attachmentCount >= MAX_ATTACHMENTS) {
        fileErrors.push(formatMessage({
          id: 'attachment-count-error'
        }, {
          n: MAX_ATTACHMENTS
        }));
        break;
      }

      // File cannot be larger than 5MB
      if (file.size > MAX_SIZE_MB) {
        fileErrors.push(formatMessage({
          id: 'attachment-size-error'
        }, {
          attachment: file.name
        }));
        continue; // eslint-disable-line no-continue
      }
      const uuid = v4();
      if (validateAttachment) {
        setIsValidating(true);
        let validation;
        try {
          validation = await Promise.resolve(validateAttachment(file, fileList.indexOf(file)));
        } catch (e) {
          fileErrors.push(formatMessage({
            id: 'attachment-fetch-error'
          }, {
            fileName: file.name
          }));
        }
        if (validation && 'isValid' in validation) {
          if (validation.isValid === true) {
            validFiles.push({
              file,
              id: uuid
            });
            attachmentCount += 1;
          } else if (validation && 'message' in validation && validation.message) {
            fileErrors.push(validation.message);
          }
        }
      } else {
        validFiles.push({
          file,
          id: uuid
        });
        attachmentCount += 1;
      }
    }
    setErrors(errorMessage ? [errorMessage, ...fileErrors] : fileErrors);
    setIsValidating(false);
    if (validFiles.length) {
      setAttachments([...attachments, ...validFiles]);
      onAttach === null || onAttach === void 0 || onAttach(event, fileList);
    }
  }, [attachments, errorMessage, formatMessage, onAttach, validateAttachment]);
  useEffect(() => {
    if (attachments.length) {
      var _document$getElementB;
      const attachmentIndex = attachments.length - 1;
      const attachmentToFocus = `${attachments[attachmentIndex].file.name}-${attachmentIndex}`;
      (_document$getElementB = document.getElementById(attachmentToFocus)) === null || _document$getElementB === void 0 || _document$getElementB.focus();
    }
  }, [attachments]);
  const handleDragOver = event => {
    setActiveDrag(true);
    event.preventDefault();
  };
  const handleDragLeave = event => {
    setActiveDrag(false);
    event.preventDefault();
  };
  const handleDrop = useCallback(event => {
    event.preventDefault();
    setActiveDrag(false);
    const fileList = event.type === 'drop' ? event.dataTransfer.files : event.target.files;
    const fileNames = [...fileList].filter(file => verifyAttachmentType(file, acceptedAttachmentTypes)); // ?
    handleFileUpload(event, fileNames);
  }, [acceptedAttachmentTypes, handleFileUpload]);
  return /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId('chat-box-input')
  }, ({
    getDataTaktAttributes
  }) => /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Container, {
    id: id,
    disabled: disabled,
    onDrop: handleDrop,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    $drag: isActiveDrag
  }, /*#__PURE__*/React__default.createElement(InputGroup, {
    inputGroupRef: inputRef
  }, /*#__PURE__*/React__default.createElement(StyledTextArea, _extends({}, rest, getDataTaktAttributes({
    taktIdSuffix: 'text-area'
  }), {
    id: `${id}-textarea`,
    ref: textAreaRef,
    value: inputValue,
    placeholder: placeholder,
    onChange: handleChange,
    onKeyDown: handleKeydown,
    disabled: disabled,
    rows: 1
  }))), /*#__PURE__*/React__default.createElement(AttachmentRow, null, attachments.length ? attachments.map((attachment, index) => /*#__PURE__*/React__default.createElement(AttachmentPill, {
    key: `${attachment.file.name}-${attachment.id}`,
    attachment: attachment.file,
    onClose: () => handleAttachmentClose(attachment.file, index),
    disabled: disabled
  })) : null, isValidating && /*#__PURE__*/React__default.createElement(ValidationLoadingWrapper, null, /*#__PURE__*/React__default.createElement(InlineMessage, {
    messageType: "loading",
    message: formatMessage({
      id: 'attachment-validate-message'
    })
  }))), /*#__PURE__*/React__default.createElement(ActionBarWrapper, null, /*#__PURE__*/React__default.createElement(ActionBar, null, renderActionBar ? renderActionBar() : /*#__PURE__*/React__default.createElement(FileAttachmentButton, {
    id: `${id}-file-upload-button`,
    onChange: handleFileUpload,
    disabled: isValidating || disabled,
    acceptedAttachmentTypes: acceptedAttachmentTypes
  })), /*#__PURE__*/React__default.createElement(SendButton, {
    onClick: handleSend,
    disabled: isValidating || disabled,
    isActive: !!attachments.length || !!inputValue.length
  }))), /*#__PURE__*/React__default.createElement(MessageContainer, null, errors.map(error => /*#__PURE__*/React__default.createElement(ValidationLine, {
    role: "alert",
    "aria-live": "assertive",
    id: `${id}-chatbox-error`
  }, /*#__PURE__*/React__default.createElement(ChatBoxValidationMessage, {
    messageType: "error",
    message: error
  }))))));
};
const ChatBoxInput = ({
  locale,
  ...rest
}) => {
  const translations = getTranslationComponent(locale);
  return /*#__PURE__*/React__default.createElement(IntlProvider, {
    translations: translations
  }, /*#__PURE__*/React__default.createElement(ThemeProvider, {
    theme: overrideMouseTimeout
  }, /*#__PURE__*/React__default.createElement(ChatBox, rest)));
};

export { ChatBoxInput as C };
//# sourceMappingURL=ChatBoxInput-DBPCpBK_.js.map
